import { Box } from "@chakra-ui/react";
import { baseColors, getCdnImageSrc, Image, Spacing } from "goi_common";
import { ReactNode } from "react";

import { ConsultantIllustration } from "@/components/common/ConsultantIllustration";
import { JangjiResultCell } from "@/utils/jangjiCell";

/**
 * 스텝 가이드의 스텝별 본문 (실험 #336 B안).
 *
 * 셸(`JangjiResultStepGuide`)이 프레임, 헤더, dot, 네비를 그리고 여기가 내용만 만든다.
 *
 * ── 수치는 표본 대표값이다
 *
 * 시설마다 달라지는 값이 아니라 표본에서 뽑은 대표값을 쓴다. 화면에 표본 수를 함께 적어두면
 * (SourceNote) 나중에 숫자를 의심할 때 어디서 왔는지 바로 찾는다.
 *
 * 봉안당 비교 (facilities_detail.json, excludeFacilities 제외)
 *   각 시설의 중위 요금을 그 시설 대표가로 잡고, 그 분포의 중위를 쓴다.
 *   공설 116곳 중위 30만원 / 사설 199곳 중위 395만원
 *
 * 사설 자리 폭 (가격이 셋 이상인 사설 봉안당 146곳)
 *   최저 150만원 / 중간 435만원 / 최고 924만원.
 *   시안은 "눈높이 층 720, 중간 520, 상단 380" 이었지만 층수 정보가 데이터에 없어서
 *   "가장 비싼 자리 / 중간 / 가장 싼 자리" 로 라벨을 바꿨다.
 *
 * 장례식장 (parlors_detail.json 1,186곳의 expected_charge_sum)
 *   중위는 무빈소 88 / 가족장 424 / 일반장 822 만원이지만 **화면에는 범위를 쓴다.**
 *   무빈소 38~145 / 가족장 298~655 / 일반장 653~1,205 만원(최저 시설 ~ 최고 시설).
 *   중간값만 적으면 시설 간 차이가 없는 것처럼 보인다. 같은 일반장인데 양구군 653 과
 *   여의도성모 1,205 가 552만원 벌어진다. p25~p75 는 86만원이지만 그건 양 끝을 잘라낸 값이라
 *   시설 선택의 폭을 재는 데 쓰면 안 된다.
 *
 * 화장장 (hwajang_detail.json 63곳)
 *   관내 중위 10만원, 관외 중위 60만원(각 n=59). **화면에는 쓰지 않는다.** 남은 세 단계의
 *   무게는 장례식장과 상조에서 나오고 화장장은 자릿수가 다르다. 흐름 박스가 금액을 비워 둔
 *   이유이기도 하다.
 *
 * 이장 비율 (consult_jangjirecommendrequest)
 *   status='이장' 이 20.7%(4,643/22,474, 2026-01-01~).
 *   **"5명 중 1명이 옮긴다" 가 아니다.** 우리에게 오는 장지 상담 중 이장 목적의 비중이고,
 *   애초에 옮기려는 사람이 우리를 찾는 선택 편향이 있다. 그래서 주어를 "장지 상담" 으로
 *   고정했다. 바꾸지 말 것.
 *
 * 상조 선불제/후불제 (constants/servicePackageDetails, StepGuideContainer 의 비교표)
 *   고이 가족장 279만원 vs 같은 구성 선불제 499만원. 220만원 차이.
 *   선불제 값은 선불제 상조 상품 중 저렴한 쪽 기준이라 실제 차이는 더 벌어질 수 있다.
 *
 * 이제 임시 수치는 없다. 숫자를 새로 넣을 때도 SourceNote 로 표본을 함께 밝힐 것.
 */

interface StepArgs {
  facilityName?: string;
  regionLabel?: string;
}

/**
 * 실데이터 근거 문구.
 *
 * TempBadge 와 역할이 반대다. 배지는 "아직 근거가 없다" 는 표시고, 이건 표본을 밝히는
 * 것이다. 화면에 표본 수를 적어두면 나중에 숫자를 의심할 때 어디서 왔는지 바로 찾는다.
 */
const SourceNote = ({ children }: { children: ReactNode }) => (
  <Box fontSize="12px" color={baseColors.label_assistive_1} lineHeight="1.6">
    {children}
  </Box>
);

/** 스텝 헤드라인. 강조어는 <b> 로 감싸 주황이 된다 */
const StepHeadline = ({ children }: { children: ReactNode }) => (
  <Box
    fontSize="24px"
    fontWeight={700}
    lineHeight="1.45"
    letterSpacing="-0.02em"
    color={baseColors.label_normal}
    sx={{ "& b": { color: baseColors.primary_orange, fontWeight: 700 } }}
  >
    {children}
  </Box>
);

const StepBody = ({ children }: { children: ReactNode }) => (
  <Box
    fontSize="14px"
    lineHeight="1.75"
    color={baseColors.label_alternative_1}
    sx={{ "& b": { fontWeight: 700, color: baseColors.label_normal } }}
  >
    {children}
  </Box>
);

/** 흰 카드 (테두리 있는 정보 블록) */
const Card = ({ children, gap = "6px" }: { children: ReactNode; gap?: string }) => (
  <Box
    display="flex"
    flexDirection="column"
    gap={gap}
    padding="20px"
    borderRadius="12px"
    border={`1px solid ${baseColors.line_normal}`}
  >
    {children}
  </Box>
);

/** 스텝 컨테이너. padding-top 으로 스텝별 시작 높이를 맞춘다 */
const Step = ({
  paddingTop = "8px",
  gap = "22px",
  children,
}: {
  paddingTop?: string;
  gap?: string;
  children: ReactNode;
}) => (
  <Box display="flex" flexDirection="column" gap={gap} paddingTop={paddingTop}>
    {children}
  </Box>
);

/** 스텝 0. 두 셀이 안내 문구만 다르다 */
const PreparingStep = ({ facilityName, reason }: { facilityName?: string; reason: string }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    gap="20px"
    paddingTop="56px"
    textAlign="center"
  >
    {/*
     * 상담원 일러스트. /match/recommendation/[id] 견적 준비 화면이 쓰는 것과 같은 그림이라
     * "지금 사람이 견적을 보고 있다" 는 상태가 바로 읽힌다. 시안의 정적 링은 로딩 스피너처럼
     * 보이는데 실제로 로딩되는 것이 없어서 오해를 준다.
     */}
    <ConsultantIllustration size={72} />
    <Box
      fontSize="24px"
      fontWeight={700}
      lineHeight="1.5"
      letterSpacing="-0.02em"
      color={baseColors.label_normal}
    >
      입력하신 조건으로
      <br />
      <Box as="span" color={baseColors.primary_orange}>
        {facilityName ?? "선택하신 장지"}
      </Box>
      <br />
      견적을 준비하고 있어요
    </Box>
    <Box
      padding="16px"
      borderRadius="12px"
      backgroundColor={baseColors.fill_normal}
      fontSize="14px"
      lineHeight="1.75"
      color={baseColors.label_alternative_1}
    >
      이 시설은 {reason} 직접 확인해야 해서{" "}
      <Box as="span" fontWeight={700} color={baseColors.label_normal}>
        전문 상담이 필요해요.
      </Box>
      <br />
      남겨주신 응답을 바탕으로 견적을 전달드릴 예정이에요.
    </Box>
    <Box fontSize="12px" lineHeight="1.7" color={baseColors.label_assistive_1}>
      장례 준비에 도움되는 내용만 담았어요.
      <br />
      1분만에 확인해보세요.
    </Box>
  </Box>
);

/** 공설/사설 대비 카드 한 장 */
const KindCard = ({
  title,
  value,
  desc,
  accent,
}: {
  title: string;
  value: string;
  desc: string;
  accent?: boolean;
}) => (
  <Box
    display="flex"
    flexDirection="column"
    gap="8px"
    padding="20px"
    borderRadius="12px"
    border={`1px solid ${accent ? baseColors.primary_orange : baseColors.line_normal}`}
    backgroundColor={accent ? "#FFF9F4" : baseColors.white}
  >
    <Box display="flex" alignItems="baseline" justifyContent="space-between" gap="8px">
      <Box fontSize="13px" fontWeight={700} color={baseColors.label_normal}>
        {title}
      </Box>
      <Box
        fontSize="17px"
        fontWeight={800}
        color={accent ? baseColors.primary_orange : baseColors.label_alternative_1}
      >
        {value}
      </Box>
    </Box>
    <Box fontSize="12px" lineHeight="1.7" color={baseColors.label_alternative_1}>
      {desc}
    </Box>
  </Box>
);

const FLOW_STEPS: { label: string; basis: string; value?: string; done?: boolean }[] = [
  { label: "장지", basis: "공설 봉안당", value: "30만원", done: true },
  { label: "장례식장", basis: "빈소, 음식" },
  { label: "화장장", basis: "화장 사용료" },
  { label: "상조", basis: "인력, 차량, 용품" },
];

/**
 * 장지 -> 장례식장 -> 화장장 -> 상조 흐름.
 *
 * **장지에만 금액이 있고 남은 셋은 비워 둔다.** 넷 다 금액을 적으면 "장례식장 424만원" 이
 * 확정처럼 읽히고, 그걸 본 사람은 견적을 받을 이유가 사라진다. 남은 셋은 아직 정하지 않은
 * 단계라서 실제로도 확정값이 없다. 대신 각 단계에 무엇이 들어가는지(빈소, 음식 / 인력, 차량,
 * 용품)를 적어 금액 없이도 무게가 읽히게 했다.
 *
 * 장지 행만 테두리와 "확인 완료" 배지를 받는다. 한 칸은 끝났고 세 칸이 남았다는 것이 이 스텝의
 * 전부다.
 */
const FlowBox = () => (
  <Box padding="16px" borderRadius="12px" backgroundColor={baseColors.fill_normal}>
    {FLOW_STEPS.map((step, index) => (
      <Box key={step.label}>
        {index > 0 && (
          <Box
            padding="4px 0"
            textAlign="center"
            fontSize="11px"
            color={baseColors.label_assistive_1}
          >
            ↓
          </Box>
        )}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="8px"
          padding="11px 14px"
          borderRadius="8px"
          backgroundColor={baseColors.white}
          border={`1px solid ${step.done ? baseColors.primary_orange : baseColors.line_normal}`}
        >
          <Box display="flex" alignItems="center" gap="6px">
            <Box
              fontSize="14px"
              fontWeight={700}
              color={step.done ? baseColors.orange_600 : baseColors.label_normal}
            >
              {step.label}
            </Box>
            <Box fontSize="11px" color={baseColors.label_assistive_1}>
              {step.basis}
            </Box>
            {step.done && (
              <Box
                padding="2px 6px"
                borderRadius="4px"
                backgroundColor={baseColors.orange_100}
                fontSize="11px"
                fontWeight={700}
                color={baseColors.orange_600}
              >
                확인 완료
              </Box>
            )}
          </Box>
          {step.value && (
            <Box
              fontSize="15px"
              fontWeight={800}
              color={step.done ? baseColors.orange_600 : baseColors.label_alternative_1}
            >
              {step.value}
            </Box>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

/** 3열 비교표 (조문객 x 시설 A/B) */
const CompareTable = ({
  head,
  rows,
}: {
  head: [string, string, string];
  rows: [string, string, string][];
}) => (
  <Box borderRadius="12px" border={`1px solid ${baseColors.line_normal}`} overflow="hidden">
    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" backgroundColor={baseColors.fill_normal}>
      {head.map((h, i) => (
        <Box
          key={h}
          padding="10px 8px"
          fontSize="12px"
          fontWeight={700}
          color={baseColors.label_alternative_1}
          textAlign={i === 0 ? "left" : "right"}
        >
          {h}
        </Box>
      ))}
    </Box>
    {rows.map((row, rowIndex) => (
      <Box
        key={row[0]}
        display="grid"
        gridTemplateColumns="1fr 1fr 1fr"
        borderTop={`1px solid ${baseColors.line_normal}`}
        backgroundColor={rowIndex % 2 === 1 ? "#FCFCFC" : baseColors.white}
      >
        {row.map((cell, i) => (
          <Box
            key={i}
            padding="11px 8px"
            fontSize="13px"
            fontWeight={i === 0 ? 700 : 600}
            color={i === 2 ? baseColors.primary_orange : baseColors.label_normal}
            textAlign={i === 0 ? "left" : "right"}
          >
            {cell}
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

/**
 * 마지막 스텝. 두 셀 공통.
 *
 * 카피와 이미지를 `MatchRequestOnboarding`(match/request?template=cost 의 온보딩 화면)에서
 * 그대로 가져왔다. 같은 약속을 하는 화면이라 문구가 갈리면 안 되고, 손 일러스트도 이미
 * CDN 에 있어서 새로 올릴 필요가 없다.
 *
 * 다른 점은 버튼뿐이다. 그쪽은 "무료로 예상 비용 확인하기" 로 바로 퍼널을 시작하고, 여기는
 * 셸(JangjiResultStepGuide)의 마지막 스텝 버튼이 그 역할을 한다.
 */
const TotalCostStep = () => (
  <Step paddingTop="8px" gap="0px">
    <Box
      className="title_3_sb"
      color={baseColors.label_normal}
      textAlign="center"
      whiteSpace="pre-line"
    >
      내 상황에 맞는
      <br />
      장례 비용, 얼마나 들까요?
    </Box>

    <Spacing gap={16} />

    <Box className="body_1_normal_r" color={baseColors.label_alternative_1} textAlign="center">
      지역과 상황에 따라 비용이 크게 달라요.
      <br />
      몇 가지만 선택하면 예상 비용과
      <br />
      줄일 수 있는 항목까지 알려드릴게요.
    </Box>

    <Spacing gap={32} />

    {/*
     * 폭을 100% 로 두면 430px 화면에서 이미지가 340px 넘게 차지해 그 아래 문구가 하단 고정
     * 영역(그라데이션 + dot) 뒤로 들어간다. 장식 이미지라 줄여도 잃는 게 없다.
     */}
    <Box display="flex" justifyContent="center" width="100%">
      <Box maxWidth="230px" width="100%">
        <Image
          src={getCdnImageSrc("direct/registration/post-signup/content-guide.png")}
          alt="장례 예상 비용 안내"
          width="100%"
        />
      </Box>
    </Box>

    <Spacing gap={20} />

    <Box className="caption_1_r" color={baseColors.label_alternative_1} textAlign="center">
      30초면 전체 견적을 받아볼 수 있어요.
    </Box>
  </Step>
);

/* ============================ 공설 ============================ */

const publicSteps = ({ facilityName, regionLabel }: StepArgs): ReactNode[] => [
  <PreparingStep key="0" facilityName={facilityName} reason="자격 조건과 잔여 자리를" />,

  <Step key="1">
    <StepHeadline>
      장지는 크게
      <br />
      <b>공설과 사설</b>로 나뉩니다
    </StepHeadline>
    <KindCard
      title="공설 (지자체 운영)"
      value="30만원"
      desc="비용이 낮습니다. 대신 그 지역 주민이어야 하고, 정해진 기수 안에서만 안치가 가능합니다."
      accent
    />
    <KindCard
      title="사설 (법인이나 재단 운영)"
      value="395만원"
      desc="안치 기간과 시설, 접근성이 좋습니다. 대신 자리에 따라 가격 차이가 큽니다."
    />
    <SourceNote>전국 봉안당 315곳 요금의 중간값 (공설 116곳, 사설 199곳)</SourceNote>
  </Step>,

  <Step key="2">
    <StepHeadline>
      공설인 <b>{regionLabel ? `${regionLabel} 봉안당` : "이 장지"}</b>을
      <br />
      합리적으로 잘 알아보고 계십니다
    </StepHeadline>
    <StepBody>다만 장례 준비는 장지로만 끝나지 않습니다.</StepBody>
    <FlowBox />
    <StepBody>
      장지는 준비하고 계시지만 남은 세 가지 항목에서 <b>수백만원 혹은 수천만원</b> 넘게 남았을 수
      있습니다.
    </StepBody>
  </Step>,

  <Step key="3">
    {/*
     * 축이 둘이고 표는 둘을 한 번에 보여준다. 행 사이가 조문객 규모, 행 안의 폭이 시설 차이다.
     *
     *   조문객 규모  무빈소 -> 일반장 중위 차액 735만원 (같은 시설 안에서 재도 같다)
     *   시설 차이    같은 일반장인데 653만원(양구군) ~ 1,205만원(여의도성모) = 552만원
     *
     * 처음에 시설 차이를 p25~p75 로 재서 86만원이라고 적어 뒀는데 그게 틀렸다. 가운데 50%가
     * 780~865 에 몰려 있는 건 맞지만 그 구간은 양 끝을 잘라낸 값이다. 지방 소규모와 서울
     * 대형병원을 비교하면 552만원이 벌어진다. **중간값 한 개로 표를 만들지 말 것.**
     */}
    <StepHeadline>
      장례식장을 어떻게 정하느냐에 따라
      <br />
      <b>700만원 넘게</b> 차이가 날 수도 있습니다
    </StepHeadline>
    <StepBody>
      조문객 규모에서 크게 갈리고, 같은 규모에서도 장례식장에 따라 다시 벌어집니다. 빈소 사용료와
      음식 단가가 시설마다 다릅니다.
    </StepBody>
    <CompareTable
      head={["조문객 규모", "빈소", "예상 비용"]}
      rows={[
        ["받지 않음", "무빈소", "38~145만원"],
        ["가족, 친지", "가족장", "298~655만원"],
        ["지인까지", "일반장", "653~1,205만원"],
      ]}
    />
    <SourceNote>전국 장례식장 1,186곳 중 최저 시설과 최고 시설의 예상 비용</SourceNote>
  </Step>,

  <Step key="4">
    <StepHeadline>
      장례지도사와 차량, 용품까지
      <br />
      정하는 상조 서비스는
      <br />
      <b>220만원 차이가 날 수도</b> 있습니다
    </StepHeadline>
    <StepBody>
      상조 서비스 하나에 장례지도사와 도우미, 리무진, 수의와 관이 다 들어갑니다. 같은 구성인데
      선불제와 후불제의 총액이 220만원 차이 납니다.
    </StepBody>
    <CompareTable
      head={["구분", "선불제", "후불제"]}
      rows={[
        ["가족장 총액", "499만원", "279만원"],
        ["납입 방식", "미리 납입", "장례 후 결제"],
        ["품목 구성", "같음", "같음"],
      ]}
    />
  </Step>,

  <TotalCostStep key="5" />,
];

/* ============================ 사설 ============================ */

const privateSteps = ({ facilityName }: StepArgs): ReactNode[] => [
  <PreparingStep key="0" facilityName={facilityName} reason="자리 등급과 잔여 여부를" />,

  <Step key="1">
    <StepHeadline>
      장지는 크게
      <br />
      <b>공설과 사설</b>로 나뉩니다
    </StepHeadline>
    <KindCard
      title="공설 (지자체 운영)"
      value="짧은 안치기간"
      desc="비용이 낮습니다. 대신 관내 거주 요건이 있고, 자리가 없으면 대기해야 합니다."
    />
    <KindCard
      title="사설 (법인이나 재단 운영)"
      value="좋은 시설, 긴 안치기간"
      desc="시설과 관리 상태가 좋고 안치 기간이 길며, 접근성도 좋습니다. 대신 자리 조건에 따라 차이가 큽니다."
      accent
    />
  </Step>,

  <Step key="2">
    <StepHeadline>
      사설은 <b>어떤 자리인지</b>에 따라
      <br />
      가격이 달라집니다
    </StepHeadline>
    <StepBody>
      봉안당은 부동산과 비슷합니다. 같은 시설 안에서도 어느 자리인지에 따라 가격이 여섯 배까지
      벌어집니다.
    </StepBody>
    <Card gap="0px">
      {[
        { label: "가장 비싼 자리", value: "924만원", accent: true },
        { label: "중간 자리", value: "435만원", accent: false },
        { label: "가장 싼 자리", value: "150만원", accent: false },
      ].map((row, index) => (
        <Box
          key={row.label}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding="10px 0"
          borderTop={index === 0 ? "none" : `1px solid ${baseColors.line_normal}`}
        >
          <Box fontSize="13px" fontWeight={700} color={baseColors.label_normal}>
            {row.label}
          </Box>
          <Box
            fontSize="17px"
            fontWeight={800}
            color={row.accent ? baseColors.primary_orange : baseColors.label_alternative_1}
          >
            {row.value}
          </Box>
        </Box>
      ))}
    </Card>
    <StepBody>수목장과 잔디장은 구획 위치와 사용 기간으로 나뉩니다.</StepBody>
    <SourceNote>가격이 셋 이상인 사설 봉안당 146곳의 최저, 중간, 최고 요금 중위값</SourceNote>
  </Step>,

  <Step key="3">
    <StepHeadline>
      장지는 한 번 정하면
      <br />
      <b>되돌리기 어렵습니다</b>
    </StepHeadline>
    {/*
     * 20.7% 는 consult_jangjirecommendrequest 의 status='이장' 비중 실측이다
     * (4,643 / 22,474, 2026-01-01~).
     *
     * 주어가 "장지 상담" 인 것이 중요하다. "급하게 정한 사람 5명 중 1명이 옮긴다" 가 아니다.
     * 우리에게 오는 상담 중 이장 목적의 비중이고, 옮기려는 사람이 우리를 찾는 선택 편향이 있다.
     */}
    <Card>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap="8px">
        <Box fontSize="13px" fontWeight={700} color={baseColors.label_normal}>
          장지 상담 5건 중 1건
        </Box>
        <Box fontSize="24px" fontWeight={800} color={baseColors.primary_orange} lineHeight="1">
          20.7%
        </Box>
      </Box>
      <Box fontSize="12px" lineHeight="1.7" color={baseColors.label_alternative_1}>
        <Box as="span" fontWeight={700} color={baseColors.label_normal}>
          이미 모신 분을 다시 옮기려는 문의
        </Box>
        입니다. 급하게 정한 자리가 결국 다시 옮기는 일로 이어집니다.
      </Box>
    </Card>
    <Box display="grid" gridTemplateColumns="1fr 1fr" gap="8px">
      <Box
        display="flex"
        flexDirection="column"
        gap="6px"
        padding="14px"
        borderRadius="12px"
        backgroundColor={baseColors.fill_normal}
      >
        <Box fontSize="13px" fontWeight={700} color={baseColors.label_alternative_1}>
          장례 중에 급히 결정
        </Box>
        <Box fontSize="12px" lineHeight="1.6" color={baseColors.label_assistive_1}>
          거리가 멀거나
          <br />
          형제간 의견이 갈려
          <br />
          다시 옮기게 됩니다
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap="6px"
        padding="14px"
        borderRadius="12px"
        backgroundColor={baseColors.orange_100}
      >
        <Box fontSize="13px" fontWeight={700} color={baseColors.orange_700}>
          미리 상담 후 결정
        </Box>
        <Box fontSize="12px" lineHeight="1.6" color={baseColors.orange_700}>
          가족이 함께 보고
          <br />
          한 번에 결정
          <br />한 번으로 끝
        </Box>
      </Box>
    </Box>
  </Step>,

  <Step key="4">
    <StepHeadline>
      사실 장지는
      <br />
      장례의 <b>일부분</b>입니다
    </StepHeadline>
    <Card>
      <Box display="flex" alignItems="baseline" justifyContent="space-between" gap="8px">
        <Box fontSize="13px" fontWeight={700} color={baseColors.label_normal}>
          시설
        </Box>
        <Box fontSize="17px" fontWeight={800} color={baseColors.primary_orange}>
          최대 1,205만원
        </Box>
      </Box>
      <Box fontSize="12px" lineHeight="1.7" color={baseColors.label_alternative_1}>
        장례식장과 화장장. 조문객 200명 기준으로 빈소 규모와 음식 단가에 따라 여기까지 올라갑니다.
      </Box>
    </Card>
    <Card>
      <Box display="flex" alignItems="baseline" justifyContent="space-between" gap="8px">
        <Box fontSize="13px" fontWeight={700} color={baseColors.label_normal}>
          상조 서비스{" "}
          <Box as="span" fontWeight={400} color={baseColors.label_assistive_1}>
            (인력, 차량, 용품)
          </Box>
        </Box>
        <Box fontSize="17px" fontWeight={800} color={baseColors.primary_orange}>
          220만원 차이
        </Box>
      </Box>
      <Box fontSize="12px" lineHeight="1.7" color={baseColors.label_alternative_1}>
        같은 구성인데 선불제는 가족장 499만원, 고이 후불제는 279만원입니다.
      </Box>
    </Card>
    <Box
      padding="14px 16px"
      borderRadius="12px"
      backgroundColor={baseColors.orange_100}
      fontSize="12px"
      lineHeight="1.7"
      color={baseColors.orange_700}
    >
      여기서{" "}
      <Box as="span" fontWeight={700}>
        불필요한 비용을 제외하여
      </Box>{" "}
      장지 부담을 덜 수 있습니다.
    </Box>
    <SourceNote>
      전국 장례식장 1,186곳 예상 비용과 고이 가족장 상품가, 같은 구성 선불제 상조 비교 기준
    </SourceNote>
  </Step>,

  <TotalCostStep key="5" />,
];

export const JANGJI_STEP_BODIES: Record<JangjiResultCell, (args: StepArgs) => ReactNode[]> = {
  public: publicSteps,
  private: privateSteps,
};
